/**
 * Policies (ARCHITECTURE.md §6, strategic memory; Phase 6 groundwork): the owner's standing word
 * on how the company runs, by key, versioned. Written here, read whole by agents by key. Every
 * change is a new version with the old text kept; retiring keeps the text too.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type PolicyJson, type PolicyRevisionJson, type User } from '../../api.js';

const CATEGORIES = [
  'goals',
  'acquisition',
  'reserves',
  'pricing',
  'operations',
  'guest_service',
  'finance',
  'other',
] as const;

export function PoliciesPage(): ReactNode {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stored, setStored] = useState<string | null>(null);
  const [draft, setDraft] = useState({ key: '', title: '', category: 'other', body: '' });

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<{ user: User | null }>('/api/me'),
    retry: false,
  });
  const isOwner = me.data?.user?.roles.includes('owner') ?? false;

  const policies = useQuery({
    queryKey: ['knowledge', 'policies'],
    queryFn: () =>
      api.get<{ policies: PolicyJson[] }>('/api/knowledge/policies?include_inactive=true'),
  });
  const detail = useQuery({
    queryKey: ['knowledge', 'policy', selected],
    enabled: selected !== null,
    queryFn: () =>
      api.get<{ policy: PolicyJson; revisions: PolicyRevisionJson[] }>(
        `/api/knowledge/policies/${selected}`,
      ),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['knowledge', 'policies'] });
    void queryClient.invalidateQueries({ queryKey: ['knowledge', 'policy'] });
  };
  const save = useMutation({
    mutationFn: (body: unknown) =>
      api.put<{ policy: PolicyJson; changed: boolean }>('/api/knowledge/policies', body),
    onSuccess: (result) => {
      setError(null);
      setStored(
        result.changed
          ? `Saved ${result.policy.key}, version ${result.policy.version}.`
          : `${result.policy.key} was already exactly this; nothing written.`,
      );
      setSelected(result.policy.key);
      invalidate();
    },
    onError: (err: Error) => setError(err.message),
  });
  const setActive = useMutation({
    mutationFn: (input: { key: string; isActive: boolean }) =>
      api.post<{ policy: PolicyJson }>(`/api/knowledge/policies/${input.key}/active`, {
        isActive: input.isActive,
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err: Error) => setError(err.message),
  });

  const edit = (p: PolicyJson): void => {
    setSelected(p.key);
    setDraft({ key: p.key, title: p.title, category: p.category, body: p.body });
    setStored(null);
  };

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    save.mutate(draft);
  };

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Policies</h1>
          <p className="subtitle">
            Your standing word on how the company runs — goals, reserves, pricing, guest service.
            Agents read a policy whole, by its key. Every change keeps the old version.{' '}
            <Link to="/knowledge">Back to Knowledge</Link>.
          </p>
        </div>
      </header>

      {policies.isError && <p className="error">{policies.error.message}</p>}
      {policies.data && policies.data.policies.length === 0 && (
        <div className="card empty">
          <h2>No policies yet.</h2>
          <p>
            Write the first one below; in development <code>pnpm knowledge:seed-mock</code> loads
            samples.
          </p>
        </div>
      )}
      {policies.data && policies.data.policies.length > 0 && (
        <section className="card">
          <h2>Policies</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Title</th>
                <th>Category</th>
                <th className="num">Version</th>
                <th>Embedded</th>
                <th>Status</th>
                {isOwner && <th />}
              </tr>
            </thead>
            <tbody>
              {policies.data.policies.map((p) => (
                <tr key={p.key} className={p.is_active ? '' : 'muted'}>
                  <th scope="row">
                    <button type="button" className="link" onClick={() => edit(p)}>
                      {p.key}
                    </button>
                  </th>
                  <td>{p.title}</td>
                  <td>{p.category.replace(/_/g, ' ')}</td>
                  <td className="num">{p.version}</td>
                  <td className="small">{p.has_embedding ? p.embedding_model : 'not yet'}</td>
                  <td>{p.is_active ? 'active' : 'retired'}</td>
                  {isOwner && (
                    <td>
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => setActive.mutate({ key: p.key, isActive: !p.is_active })}
                      >
                        {p.is_active ? 'Retire' : 'Bring back'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {detail.data && (
        <section className="card">
          <h2>
            {detail.data.policy.title}{' '}
            <span className="muted small">
              · {detail.data.policy.key} · version {detail.data.policy.version}
            </span>
          </h2>
          <p style={{ whiteSpace: 'pre-wrap' }}>{detail.data.policy.body}</p>
          {detail.data.revisions.length > 1 && (
            <details>
              <summary className="muted small">{detail.data.revisions.length} versions</summary>
              {detail.data.revisions.map((r) => (
                <article key={r.version}>
                  <h3 className="small">
                    Version {r.version} · {new Date(r.edited_at).toLocaleString()}
                  </h3>
                  <p className="small" style={{ whiteSpace: 'pre-wrap' }}>
                    {r.body}
                  </p>
                </article>
              ))}
            </details>
          )}
        </section>
      )}

      {isOwner && (
        <section className="card">
          <h2>
            {draft.key && policies.data?.policies.some((p) => p.key === draft.key)
              ? 'New version'
              : 'Write a policy'}
          </h2>
          <form onSubmit={onSubmit} className="form-grid">
            <label>
              Key{' '}
              <input
                value={draft.key}
                onChange={(e) => setDraft({ ...draft, key: e.target.value })}
                placeholder="reserves.cash_floor"
                pattern="^[a-z0-9]+(\.[a-z0-9_]+)*$"
                required
              />
            </label>
            <label>
              Title{' '}
              <input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                maxLength={200}
                required
              />
            </label>
            <label>
              Category{' '}
              <select
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </label>
            <label>
              The policy{' '}
              <textarea
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                rows={8}
                maxLength={20000}
                required
              />
            </label>
            <button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => {
                setDraft({ key: '', title: '', category: 'other', body: '' });
                setSelected(null);
              }}
            >
              Clear
            </button>
          </form>
          {error && <p className="error">{error}</p>}
          {stored && <p className="muted">{stored}</p>}
          <p className="muted small">
            A key is lowercase words joined by dots, and it never changes; the words do, one version
            at a time, and each version is audited with your name.
          </p>
        </section>
      )}
    </div>
  );
}
